import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  BellIcon,
  ChevronRightIcon,
  ClockIcon,
  LockClosedIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import {
  AppPage, BTN_PRIMARY, CARD, Eyebrow, HeroPanel, Segmented,
} from '../components/UI/AppUI';
import { subjectLink } from '../components/Layout2/Sidebar2';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` });
const getJSON = (path) => fetch(`${API_URL}${path}`, { headers: authHeaders() })
  .then((r) => (r.ok ? r.json() : null)).catch(() => null);
const listOf = (d) => d?.results || (Array.isArray(d) ? d : []);

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

const DOT = { red: 'bg-rose-500', amber: 'bg-amber-500', green: 'bg-emerald-500', gray: 'bg-slate-400' };

// Default course: the last one viewed, else an owned one (DLR first when several are owned)
const pickCourse = (courses) => {
  let saved = null;
  try { saved = Number(localStorage.getItem('dashboard2_course')); } catch { /* ignore */ }
  if (courses.some((c) => c.id === saved)) return saved;
  const owned = courses.filter((c) => c.is_owned);
  return (owned.find((c) => /dlr/i.test(c.name)) || owned[0] || courses[0])?.id;
};

const Bar = ({ value, className = 'bg-primary-500' }) => (
  <span className="block h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
    <span className={`block h-full rounded-full ${className}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
  </span>
);

const Stat = ({ label, value, hint }) => (
  <div className={`${CARD} p-5`}>
    <p className="text-sm text-slate-500 dark:text-gray-400">{label}</p>
    <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500 dark:text-gray-500">{hint}</p>}
  </div>
);

// Image with a code-badge fallback when there is no image or it fails to load
const Thumb = ({ src, label, className = '', children }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`relative flex-shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-primary-600 to-primary-900 ${className}`}>
      {src && !failed
        ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
        : <span className="flex h-full w-full items-center justify-center text-sm font-bold tracking-wide text-white/90">{label}</span>}
      {children}
    </span>
  );
};

// Artwork for subjects without an image: a gradient (picked from the code) with the code as a watermark
const ART = [
  'from-primary-600 via-indigo-800 to-slate-950',
  'from-sky-600 via-primary-800 to-slate-950',
  'from-indigo-500 via-violet-900 to-slate-950',
  'from-cyan-600 via-sky-900 to-slate-950',
];
const SubjectArt = ({ code = '' }) => {
  const hue = [...code].reduce((n, ch) => n + ch.charCodeAt(0), 0) % ART.length;
  return (
    <span className={`absolute inset-0 bg-gradient-to-br ${ART[hue]}`}>
      <span className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
      <span className="absolute -bottom-6 right-3 select-none text-7xl font-bold tracking-tighter text-white/10">{code}</span>
    </span>
  );
};

// Small progress ring for the image corner
const Ring = ({ value }) => {
  const r = 16;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-slate-950/50 backdrop-blur">
      <svg viewBox="0 0 40 40" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="20" cy="20" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
        <circle cx="20" cy="20" r={r} fill="none" strokeWidth="3" strokeLinecap="round"
          className={value >= 75 ? 'stroke-emerald-400' : 'stroke-primary-400'}
          strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, value) / 100)} />
      </svg>
      <span className="relative text-[11px] font-semibold tabular-nums text-white">{Math.round(value)}%</span>
    </span>
  );
};

const SubjectTile = ({ subject, gameAccess }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const isGame = subject.subject_type === 'game';
  const locked = isGame && !gameAccess[subject.game_identifier?.toUpperCase()];
  const correct = subject.progress?.questions_correct || 0;
  const attempted = subject.progress?.questions_attempted || 0;
  const total = subject.total_questions || 0;
  const pct = total ? (correct / total) * 100 : 0;
  const accuracy = attempted ? Math.round((correct / attempted) * 100) : null;
  // Names often repeat the code or course ("MIC - …", "DLR - …"); the code is already on the chip
  const title = subject.name.replace(new RegExp(`^(${subject.code}|DLR|ATPL)\\s*[-–:]\\s*`, 'i'), '');
  const chip = 'rounded-md border border-white/20 bg-black/30 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur';

  return (
    <Link
      to={locked ? '/store' : subjectLink(subject)}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-900/10 dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none dark:hover:border-primary-500/40 dark:hover:shadow-black/40"
    >
      <span className="relative block aspect-[2/1] overflow-hidden bg-slate-900">
        {subject.image && !imgFailed
          ? <img src={subject.image} alt="" loading="lazy" onError={() => setImgFailed(true)} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
          : <SubjectArt code={subject.code} />}
        <span className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/35 to-slate-950/10" />

        <span className="absolute inset-x-3 top-3 flex items-start justify-between">
          <span className={`${chip} tracking-wide`}>{subject.code}</span>
          {isGame
            ? <span className={`${chip} inline-flex items-center gap-1 ${locked ? 'text-amber-200' : ''}`}>{locked ? <><LockClosedIcon className="h-3 w-3" />Locked</> : 'Screening game'}</span>
            : pct >= 100 ? <span className="rounded-md bg-emerald-500/90 px-2 py-0.5 text-[11px] font-semibold text-white">Completed</span>
              : attempted ? <Ring value={pct} />
                : <span className={chip}>New</span>}
        </span>

        {isGame && !locked && (
          <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-lg"><PlayIcon className="ml-0.5 h-6 w-6" /></span>
          </span>
        )}

        <span className="absolute inset-x-4 bottom-3 line-clamp-2 text-base font-semibold leading-snug text-white">{title}</span>
        {!isGame && (
          <span className="absolute inset-x-0 bottom-0 h-1 bg-white/10">
            <span className={`block h-full ${pct >= 75 ? 'bg-emerald-400' : 'bg-primary-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </span>
        )}
      </span>

      <span className="flex items-center justify-between gap-3 px-4 py-3 text-xs">
        <span className="text-slate-500 dark:text-gray-400">
          {isGame
            ? (locked ? 'Not in your plan' : 'Unlimited practice')
            : attempted ? `${correct.toLocaleString()} / ${total.toLocaleString()} correct` : `${total.toLocaleString()} questions`}
        </span>
        <span className={`inline-flex items-center gap-1 font-semibold ${locked ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-300'}`}>
          {isGame ? (locked ? 'Unlock' : 'Play') : accuracy !== null ? <span className="font-medium tabular-nums text-slate-600 dark:text-gray-300">{accuracy}% accuracy</span> : 'Start'}
          {(isGame || accuracy === null) && <ChevronRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />}
        </span>
      </span>
    </Link>
  );
};

const Dashboard2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courseId, setCourseId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [gameAccess, setGameAccess] = useState({});
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    getJSON('/api/courses/dashboard/stats/').then((d) => {
      setData(d);
      if (d?.courses?.length) setCourseId(pickCourse(d.courses));
      setLoading(false);
    });
    getJSON('/api/exams/sessions/').then((d) => setSessions(listOf(d)));
    getJSON('/api/exams/sessions/?status=in_progress,paused').then((d) => setOpenSession(listOf(d)[0] || null));
    getJSON('/api/courses/announcements/').then((d) => setAnnouncements(d?.announcements || []));
    getJSON('/api/payments/game-access/').then((d) => d && setGameAccess(
      Object.fromEntries(Object.entries(d).map(([k, v]) => [k, !!v?.has_access])),
    ));
  }, []);

  const chooseCourse = (id) => {
    setCourseId(id);
    try { localStorage.setItem('dashboard2_course', String(id)); } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <AppPage>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      </AppPage>
    );
  }

  const courses = data?.courses || [];
  const stats = data?.overall_stats || {};
  const course = courses.find((c) => c.id === courseId) || courses[0];
  const ownsAny = courses.some((c) => c.is_owned);
  const completed = sessions.filter((s) => s.status === 'completed');
  const scored = completed.filter((s) => s.score_percentage != null);
  const avgScore = scored.length ? Math.round(scored.reduce((n, s) => n + Number(s.score_percentage), 0) / scored.length) : null;
  const lastSession = openSession || sessions[0];
  const questionSubjects = course?.subjects?.filter((s) => s.subject_type !== 'game') || [];
  // Start from the course on screen when it is owned, else the first owned course
  const firstSubject = (course?.is_owned ? questionSubjects[0] : null)
    || courses.find((c) => c.is_owned)?.subjects?.find((s) => s.subject_type !== 'game');
  const gameSubjects = course?.subjects?.filter((s) => s.subject_type === 'game') || [];
  const courseCorrect = questionSubjects.reduce((n, s) => n + (s.progress?.questions_correct || 0), 0);
  const courseTotal = questionSubjects.reduce((n, s) => n + (s.total_questions || 0), 0);
  const firstName = user?.first_name || user?.full_name?.split(' ')[0];

  return (
    <AppPage wide>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Eyebrow>{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</Eyebrow>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-white">
            {greeting()}{firstName ? `, ${firstName}` : ''}
          </h1>
        </div>
        {!ownsAny && <Link to="/store" className={BTN_PRIMARY}>Choose a plan <ArrowRightIcon className="h-4 w-4" /></Link>}
      </div>

      {/* Continue + headline numbers */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <HeroPanel image={`${API_URL}/media/banners/banner.jpeg`} className="lg:col-span-2">
          {lastSession ? (
            <>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-300">
                {openSession ? 'Pick up where you left off' : 'Your last session'}
              </p>
              <h2 className="mt-3 max-w-xl text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">{lastSession.title}</h2>
              <p className="mt-2 text-sm text-gray-300">
                {lastSession.total_questions} questions
                {lastSession.mode ? ` · ${String(lastSession.mode).replace(/_/g, ' ')}` : ''}
                {!openSession && lastSession.score_percentage != null ? ` · scored ${Math.round(lastSession.score_percentage)}%` : ''}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(openSession ? `/exam/${lastSession.id}` : `/subjects/${lastSession.subject?.id || lastSession.subject}`)}
                  className={BTN_PRIMARY}
                >
                  <PlayIcon className="h-4 w-4" />{openSession ? 'Resume' : 'Practice again'}
                </button>
                {!openSession && lastSession.status === 'completed' && (
                  <Link to={`/exam/${lastSession.id}/results`} className="inline-flex items-center rounded-lg border border-white/30 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur hover:bg-white/20">
                    View results
                  </Link>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-300">Get started</p>
              <h2 className="mt-3 max-w-xl text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">
                {ownsAny ? 'Answer your first questions' : 'Unlock real exam questions'}
              </h2>
              <p className="mt-2 max-w-lg text-sm text-gray-300">
                {ownsAny ? 'Pick a subject below and start practising. Your progress shows up here.' : 'Choose a plan for ATPL theory, DLR screening, or both.'}
              </p>
              <div className="mt-6">
                {ownsAny && firstSubject
                  ? <Link to={subjectLink(firstSubject)} className={BTN_PRIMARY}><PlayIcon className="h-4 w-4" />Start with {firstSubject.code}</Link>
                  : <Link to="/store" className={BTN_PRIMARY}>See plans <ArrowRightIcon className="h-4 w-4" /></Link>}
              </div>
            </>
          )}
        </HeroPanel>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
          <Stat
            label="Questions answered"
            value={(stats.total_attempts || 0).toLocaleString()}
            hint={stats.total_attempts ? `${Math.round(stats.success_rate || 0)}% answered correctly` : 'Nothing answered yet'}
          />
          <Stat
            label="Exams completed"
            value={completed.length}
            hint={avgScore !== null ? `Average score ${avgScore}%` : 'Take a timed exam to see a score'}
          />
        </div>
      </div>

      {/* Courses */}
      {course && (
        <section className="mt-12">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Eyebrow>Study</Eyebrow>
            {courses.length > 1 && (
              <Segmented
                label="Course"
                size="md"
                value={course.id}
                onChange={chooseCourse}
                options={courses.map((c) => ({ value: c.id, label: c.code || c.name }))}
              />
            )}
          </div>

          {/* Course header with its image */}
          <div className={`mb-4 flex flex-col gap-5 p-4 sm:flex-row sm:items-center ${CARD}`}>
            <Thumb src={course.image || course.banner_image} label={course.code} className="h-28 w-full sm:h-24 sm:w-40" />
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl dark:text-white">{course.name}</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                {course.is_owned
                  ? `${questionSubjects.length} subjects${gameSubjects.length ? ` · ${gameSubjects.length} screening games` : ''}`
                  : course.description}
              </p>
              {course.is_owned && courseTotal > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="min-w-[8rem] max-w-md flex-1"><Bar value={(courseCorrect / courseTotal) * 100} /></span>
                  <span className="text-sm font-medium text-slate-700 tabular-nums dark:text-gray-300">
                    {courseCorrect.toLocaleString()} / {courseTotal.toLocaleString()} questions correct
                  </span>
                </div>
              )}
            </div>
          </div>

          {course.is_owned ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {questionSubjects.map((s) => <SubjectTile key={s.id} subject={s} gameAccess={gameAccess} />)}
              </div>
              {gameSubjects.length > 0 && (
                <>
                  <p className="mb-3 mt-8 text-sm font-semibold text-slate-900 dark:text-white">Screening games</p>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {gameSubjects.map((s) => <SubjectTile key={s.id} subject={s} gameAccess={gameAccess} />)}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className={`p-6 sm:p-8 ${CARD}`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10"><LockClosedIcon className="h-5 w-5 text-amber-500" /></span>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">You don&apos;t have this course yet</p>
                    <p className="text-sm text-slate-500 dark:text-gray-400">{course.subjects?.length || 0} subjects are waiting for you.</p>
                  </div>
                </div>
                <Link to="/store" className={BTN_PRIMARY}>Unlock in store <ArrowRightIcon className="h-4 w-4" /></Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {course.subjects?.map((s) => (
                  <span key={s.id} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-500 dark:border-white/10 dark:text-gray-400">
                    <span className="font-semibold text-slate-700 dark:text-gray-300">{s.code}</span> {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Recent exams + updates */}
      <div className="mt-12 grid gap-4 lg:grid-cols-3">
        <section className={`lg:col-span-2 ${CARD}`}>
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
            <h2 className="font-semibold text-slate-900 dark:text-white">Recent exams</h2>
            <ClockIcon className="h-5 w-5 text-slate-400" />
          </div>
          {sessions.length ? (
            <ul className="divide-y divide-slate-200 dark:divide-white/10">
              {sessions.slice(0, 5).map((s) => {
                const done = s.status === 'completed';
                const score = s.score_percentage != null ? Math.round(s.score_percentage) : null;
                return (
                  <li key={s.id}>
                    <Link to={done ? `/exam/${s.id}/results` : `/exam/${s.id}`} className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">{s.title}</span>
                        <span className="text-xs text-slate-500 dark:text-gray-400">
                          {s.total_questions} questions · {new Date(s.completed_at || s.started_at).toLocaleDateString()}
                        </span>
                      </span>
                      {done && score !== null ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ${score >= 75 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>{score}%</span>
                      ) : (
                        <span className="rounded-full bg-primary-500/10 px-2.5 py-0.5 text-xs font-semibold text-primary-700 dark:text-primary-300">{done ? 'Done' : 'In progress'}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-5 py-10 text-center text-sm text-slate-500 dark:text-gray-400">Your exams will show up here.</p>
          )}
        </section>

        <section className={CARD}>
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
            <h2 className="font-semibold text-slate-900 dark:text-white">Latest updates</h2>
            <BellIcon className="h-5 w-5 text-slate-400" />
          </div>
          {announcements.length ? (
            <ul className="max-h-80 space-y-4 overflow-y-auto px-5 py-4">
              {announcements.map((a) => (
                <li key={a.id} className="flex gap-3">
                  <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${DOT[a.color_class] || 'bg-primary-500'}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900 dark:text-white">{a.title}</span>
                    <span className="text-xs text-slate-500 dark:text-gray-400">{a.time_since_created}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-10 text-center text-sm text-slate-500 dark:text-gray-400">No announcements right now.</p>
          )}
        </section>
      </div>
    </AppPage>
  );
};

export default Dashboard2;
